import { useState, useEffect, useMemo, useCallback } from 'react';
import { api, ApiError, type SiteStatsResponse } from '../lib/api';
import {
  SiteResponse,
  CreateSite,
  buildSourceSlug,
  SOURCE_SLUG_PATTERN,
  SOURCE_SLUG_MAX_LENGTH,
} from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { SearchInput } from '../components/ui/search-input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';
import { DateRangeField } from '../components/date-range-field';

// --- Publisher uniques helpers (task 024-09) ---

type StatsPreset = 'this_month' | 'last_month' | 'custom';

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function thisMonthRange(): [string, string] {
  const today = todayYmd();
  return [`${today.slice(0, 7)}-01`, today];
}

function lastMonthRange(): [string, string] {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const last = new Date(d.getFullYear(), d.getMonth(), 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return [fmt(first), fmt(last)];
}

/** Publisher uniques panel — per-site unique-email counts for billing (§2.9). */
function PublisherUniquesPanel() {
  const [preset, setPreset] = useState<StatsPreset>('this_month');
  const [from, setFrom] = useState(() => thisMonthRange()[0]);
  const [to, setTo] = useState(() => thisMonthRange()[1]);
  const [siteStats, setSiteStats] = useState<SiteStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback((f: string, t: string) => {
    if (!f || !t) return;
    setLoading(true);
    api
      .getSiteStats(f, t)
      .then(setSiteStats)
      .catch(() => {
        // Non-critical — leave previous data on error.
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchStats(from, to);
  }, [fetchStats, from, to]);

  const applyPreset = (p: StatsPreset) => {
    setPreset(p);
    if (p === 'this_month') {
      const [f, t] = thisMonthRange();
      setFrom(f);
      setTo(t);
    } else if (p === 'last_month') {
      const [f, t] = lastMonthRange();
      setFrom(f);
      setTo(t);
    }
  };

  return (
    <Card className="mt-6" data-testid="publisher-uniques-panel">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <CardTitle>Publisher uniques</CardTitle>
          <div className="flex gap-1">
            {(
              [
                ['this_month', 'This month'],
                ['last_month', 'Last month'],
                ['custom', 'Custom'],
              ] as const
            ).map(([p, label]) => (
              <button
                key={p}
                type="button"
                onClick={() => applyPreset(p)}
                className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                  preset === p
                    ? 'bg-accent text-white'
                    : 'bg-paper text-faint hover:text-ink hairline'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {preset === 'custom' && (
            <div className="w-64">
              <DateRangeField
                from={from}
                to={to}
                onChange={(f, t) => {
                  setFrom(f);
                  setTo(t);
                }}
              />
            </div>
          )}
          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site</TableHead>
              <TableHead className="text-right">Unique</TableHead>
              <TableHead className="text-right">Leads</TableHead>
              <TableHead className="text-right">Rejected</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {siteStats === null || siteStats.sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                  {siteStats === null ? 'Loading…' : 'No publisher data for this period.'}
                </TableCell>
              </TableRow>
            ) : (
              siteStats.sites.map((site) => (
                <TableRow key={site.id}>
                  <TableCell className="font-medium">{site.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{site.uniqueEmails}</TableCell>
                  <TableCell className="text-right tabular-nums">{site.leads}</TableCell>
                  <TableCell className="text-right tabular-nums">{site.rejected}</TableCell>
                  {/* Export CSV — FR-A35 reconciliation download (task 024-13) */}
                  <TableCell className="text-right">
                    <button
                      type="button"
                      data-testid="site-export-csv"
                      onClick={() => window.open(api.siteExportUrl(site.id, from, to))}
                      className="rounded-md px-2 py-0.5 text-xs font-medium text-muted-foreground hover:text-foreground border border-border hover:bg-muted transition"
                    >
                      Export CSV
                    </button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

interface FormData {
  siteId: string;
  label: string;
  sourceSlug: string;
  cpl: string;
  monthlyBudget: string;
  leadLimit: string;
  invoiceDates: string;
}

const initialFormState: FormData = {
  siteId: '',
  label: '',
  sourceSlug: '',
  cpl: '',
  monthlyBudget: '',
  leadLimit: '',
  invoiceDates: '',
};

/** One INVOICES token, comma- or newline-separated (epic 055 README decision 12). */
const INVOICE_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Split the free-form invoice-dates field into trimmed, non-empty tokens. */
function splitInvoiceDates(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/**
 * Search over the two visible business fields (task 023-09): the business key
 * `siteId` and the display `label`. Same idiom as the Campaigns screen.
 */
function searchBlob(s: SiteResponse): string {
  return [s.siteId, s.label].filter(Boolean).join(' ').toLowerCase();
}

export function Sites() {
  const [sites, setSites] = useState<SiteResponse[]>([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [invoiceDatesError, setInvoiceDatesError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: toastError } = useToast();

  const loadSites = () => {
    api
      .getSites()
      .then(setSites)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadSites();
  }, []);

  /**
   * The source slug (the `utm_source` value of a CTO link, epic 024 / Q71)
   * prefills from the label while it has not been edited by hand (task 02's
   * shared `buildSourceSlug()` rule); the server applies the same rule when
   * the field is left empty.
   */
  const handleLabelChange = (label: string) => {
    setFormData((prev) => ({
      ...prev,
      label,
      sourceSlug: slugTouched ? prev.sourceSlug : buildSourceSlug(label),
    }));
  };

  const handleSlugChange = (sourceSlug: string) => {
    setSlugTouched(true);
    setFormData((prev) => ({ ...prev, sourceSlug }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const slug = formData.sourceSlug.trim();
    if (slug !== '') {
      if (!SOURCE_SLUG_PATTERN.test(slug)) {
        setSlugError('Must be lowercase letters, digits and dashes');
        return;
      }
      if (slug.length > SOURCE_SLUG_MAX_LENGTH) {
        setSlugError(`Must be at most ${SOURCE_SLUG_MAX_LENGTH} characters`);
        return;
      }
    }
    setSlugError(null);

    const invoiceTokens = splitInvoiceDates(formData.invoiceDates);
    const invalidToken = invoiceTokens.find((token) => !INVOICE_DATE_PATTERN.test(token));
    if (invalidToken) {
      setInvoiceDatesError(`Invalid date "${invalidToken}" — use YYYY-MM-DD`);
      return;
    }
    setInvoiceDatesError(null);
    setSubmitting(true);

    try {
      // An empty field is omitted so the server prefills it on create /
      // leaves the buying term unset (never sent as '' — the schema is
      // z.number()).
      const cpl = formData.cpl.trim();
      const monthlyBudget = formData.monthlyBudget.trim();
      const leadLimit = formData.leadLimit.trim();
      const data: CreateSite = {
        siteId: formData.siteId,
        label: formData.label,
        ...(slug !== '' ? { sourceSlug: slug } : {}),
        ...(cpl !== '' ? { cpl: Number(cpl) } : {}),
        ...(monthlyBudget !== '' ? { monthlyBudget: Number(monthlyBudget) } : {}),
        ...(leadLimit !== '' ? { leadLimit: Number(leadLimit) } : {}),
        ...(invoiceTokens.length > 0 ? { invoiceDates: invoiceTokens } : {}),
      };

      if (editingId) {
        await api.updateSite(editingId, data);
        success('Site updated successfully');
      } else {
        await api.createSite(data);
        success('Site created successfully');
      }

      setIsDialogOpen(false);
      setFormData(initialFormState);
      setEditingId(null);
      setSlugTouched(false);
      loadSites();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to save site');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (site: SiteResponse) => {
    setEditingId(site.id);
    setSlugTouched(false);
    setSlugError(null);
    setInvoiceDatesError(null);
    setFormData({
      siteId: site.siteId,
      label: site.label,
      sourceSlug: site.sourceSlug ?? buildSourceSlug(site.label),
      cpl: site.cpl != null ? String(site.cpl) : '',
      monthlyBudget: site.monthlyBudget != null ? String(site.monthlyBudget) : '',
      leadLimit: site.leadLimit != null ? String(site.leadLimit) : '',
      invoiceDates:
        site.invoiceDates && site.invoiceDates.length > 0 ? site.invoiceDates.join(', ') : '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(`Are you sure you want to delete "${label}"?`)) return;

    try {
      await api.deleteSite(id);
      success('Site deleted successfully');
      loadSites();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete site');
    }
  };

  const openCreateDialog = () => {
    setEditingId(null);
    setFormData(initialFormState);
    setSlugTouched(false);
    setSlugError(null);
    setInvoiceDatesError(null);
    setIsDialogOpen(true);
  };

  // Client-side keyword filter over siteId + label (task 023-09)
  const filteredSites = useMemo(() => {
    const needle = q.trim().toLowerCase();

    return sites.filter((s) => {
      if (needle && !searchBlob(s).includes(needle)) return false;
      return true;
    });
  }, [sites, q]);

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading sites: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Sites</h1>
          <p className="text-muted-foreground mt-2">Manage your site ID reference data</p>
        </div>
        <Button onClick={openCreateDialog}>Create Site</Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>All Sites</CardTitle>
            <SearchInput value={q} onChange={setQ} placeholder="Search site ID or label" />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Site ID</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Source slug</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No sites found. Create your first site to get started.
                  </TableCell>
                </TableRow>
              ) : filteredSites.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No sites match your search.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell className="font-medium">{site.siteId}</TableCell>
                    <TableCell className="text-muted-foreground">{site.label}</TableCell>
                    <TableCell className="font-mono text-muted-foreground">
                      {site.sourceSlug ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(site)}
                        className="mr-2"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(site.id, site.label)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <PublisherUniquesPanel />

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Site' : 'Create Site'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update the site details below.'
                : 'Create a new site by filling in the details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="siteId">Site ID</Label>
                <Input
                  id="siteId"
                  value={formData.siteId}
                  onChange={(e) => setFormData({ ...formData, siteId: e.target.value })}
                  required
                  placeholder="e.g., 101"
                  pattern="[a-z0-9-]+"
                  title="Site ID must contain only lowercase letters, numbers, and hyphens"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="label">Label</Label>
                <Input
                  id="label"
                  value={formData.label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  required
                  placeholder="e.g., Facebook"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sourceSlug">Source slug</Label>
                <Input
                  id="sourceSlug"
                  value={formData.sourceSlug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="e.g., facebook"
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  title="Must be lowercase letters, digits and dashes"
                />
                {slugError ? (
                  <p className="text-xs text-destructive">{slugError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Used as `utm_source` in CTO links</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="cpl">CPL ($)</Label>
                <Input
                  id="cpl"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cpl}
                  onChange={(e) => setFormData({ ...formData, cpl: e.target.value })}
                  placeholder="e.g., 5.50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="monthlyBudget">Monthly budget ($)</Label>
                <Input
                  id="monthlyBudget"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.monthlyBudget}
                  onChange={(e) => setFormData({ ...formData, monthlyBudget: e.target.value })}
                  placeholder="e.g., 5000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="leadLimit">Lead limit</Label>
                <Input
                  id="leadLimit"
                  type="number"
                  step="1"
                  min="0"
                  value={formData.leadLimit}
                  onChange={(e) => setFormData({ ...formData, leadLimit: e.target.value })}
                  placeholder="e.g., 500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoiceDates">Invoice dates</Label>
                <Textarea
                  id="invoiceDates"
                  value={formData.invoiceDates}
                  onChange={(e) => setFormData({ ...formData, invoiceDates: e.target.value })}
                  placeholder={'e.g., 2026-09-01, 2026-09-15'}
                />
                {invoiceDatesError ? (
                  <p className="text-xs text-destructive">{invoiceDatesError}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Comma- or newline-separated YYYY-MM-DD dates
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
